import React, { useState, useEffect, useCallback } from 'react';
import { Quote, AppSettings } from './types';
import { fetchQuotes, fetchSettings, getAdminToken } from './lib/api';
import { formatToDateInputString } from './lib/dateUtils';
import { DayCounter } from './components/DayCounter';
import { AdminPage } from './components/AdminPage';
import { Toast } from './components/Toast';

// Helper to shuffle quotes randomly while prioritizing today's scheduled quote if set
function randomizeQuotes(list: Quote[], todayDateString: string): Quote[] {
  const activeQuotes = list.filter((q) => q.is_active !== 0);
  const todayQuote = activeQuotes.find((q) => q.assigned_date === todayDateString);
  const remaining = activeQuotes.filter((q) => q !== todayQuote);

  // Fisher-Yates shuffle
  const shuffled = [...remaining];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  if (todayQuote) {
    return [todayQuote, ...shuffled];
  }
  return shuffled;
}

export const App: React.FC = () => {
  // Routing state
  const [currentPath, setCurrentPath] = useState<string>(() => window.location.pathname);

  // App data state
  const [settings, setSettings] = useState<AppSettings>({ since_date: '2026-09-22' });
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isD1, setIsD1] = useState(false);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [showQuote, setShowQuote] = useState(false);

  const showToast = useCallback((msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 2800);
  }, []);

  // Listen to browser navigation
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigateTo = (path: string) => {
    window.history.pushState(null, '', path);
    setCurrentPath(path);
  };

  // Initial load and route-based data loading
  useEffect(() => {
    async function loadData() {
      // 1. Fetch settings (since_date)
      const settingsResult = await fetchSettings();
      setSettings(settingsResult.settings);

      // 2. Fetch quotes depending on route
      const isAdminRoute = currentPath === '/admin' || currentPath.startsWith('/admin');
      if (isAdminRoute) {
        // Admin needs to see all quotes (including inactive/disabled ones)
        const quotesResult = await fetchQuotes(undefined, true);
        setQuotes(quotesResult.quotes);
        setIsD1(settingsResult.isD1 || quotesResult.isD1);
      } else {
        // Public page needs only active quotes, randomized (with today's quote prioritized if assigned)
        const todayString = formatToDateInputString(new Date());
        const quotesResult = await fetchQuotes(todayString, false);
        const randomized = randomizeQuotes(quotesResult.quotes, todayString);
        setQuotes(randomized);
        setCurrentIndex(0);
        setIsD1(settingsResult.isD1 || quotesResult.isD1);
      }

      if (getAdminToken()) {
        setIsAdminLoggedIn(true);
      }
    }
    loadData();
  }, [currentPath, isAdminLoggedIn]);

  // Pick next random quote
  const pickNextRandomQuote = useCallback(() => {
    if (quotes.length <= 1) return;
    setCurrentIndex((prev) => {
      let next = Math.floor(Math.random() * quotes.length);
      if (next === prev) {
        next = (prev + 1) % quotes.length;
      }
      return next;
    });
  }, [quotes.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement).tagName)) {
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        navigateTo('/admin');
      } else if (e.key === 'q' || e.key === 'Q') {
        setShowQuote((prev) => !prev);
      } else if (e.key === ' ' || e.key === 'ArrowRight') {
        pickNextRandomQuote();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pickNextRandomQuote]);

  const currentQuote = quotes[currentIndex];

  const handleNextQuote = (e: React.MouseEvent) => {
    e.stopPropagation();
    pickNextRandomQuote();
  };

  // ROUTE: /admin (Password Protected & Responsive Admin Console)
  if (currentPath === '/admin' || currentPath.startsWith('/admin')) {
    return (
      <div className="w-full min-h-screen bg-black">
        <AdminPage
          quotes={quotes}
          settings={settings}
          isD1={isD1}
          isAdminLoggedIn={isAdminLoggedIn}
          onLoginSuccess={() => setIsAdminLoggedIn(true)}
          onLogout={() => setIsAdminLoggedIn(false)}
          onQuotesUpdated={(newQuotes) => {
            setQuotes(newQuotes);
            setCurrentIndex(0);
          }}
          onSettingsUpdated={(newSettings) => {
            setSettings(newSettings);
          }}
          onNavigateHome={() => navigateTo('/')}
          showToast={showToast}
        />
        <Toast message={toastMessage} />
      </div>
    );
  }

  // ROUTE: / (Minimal Day Counter - No "admin" text on homepage)
  return (
    <div className="min-h-screen min-h-[100dvh] w-full bg-black text-white flex flex-col justify-between items-center selection:bg-zinc-800 relative px-6 py-8 overflow-hidden">
      {/* Top spacer */}
      <div className="w-full max-w-lg h-6" />

      {/* Dead Center - Clean & Minimal Day Counter configured via since_date */}
      <main className="flex-1 flex flex-col items-center justify-center my-auto z-10">
        <DayCounter sinceDate={settings.since_date} />

        {/* Optional subtle single-line quote (toggleable, randomized) */}
        {showQuote && currentQuote && (
          <div 
            onClick={handleNextQuote}
            className="mt-12 max-w-md text-center cursor-pointer select-none animate-fadeIn group"
            title="Click for next random quote"
          >
            <p className="text-sm sm:text-base font-serif italic text-zinc-400 group-hover:text-zinc-200 transition-colors">
              "{currentQuote.text}"
            </p>
            <p className="text-xs font-mono text-zinc-600 mt-2">
              — {currentQuote.author}
              {currentQuote.assigned_date && (
                <span className="ml-2 text-zinc-700">({currentQuote.assigned_date})</span>
              )}
            </p>
          </div>
        )}
      </main>

      {/* Subtle minimal bottom control (only 'quote' button, 'admin' removed from homepage) */}
      <footer className="w-full max-w-lg flex items-center justify-center z-10">
        <button
          onClick={() => setShowQuote(!showQuote)}
          className="text-[11px] font-mono tracking-wider text-zinc-700 hover:text-zinc-400 transition-colors py-2 px-4"
        >
          {showQuote ? 'hide quote' : 'quote'}
        </button>
      </footer>

      {/* Toast Notification */}
      <Toast message={toastMessage} />
    </div>
  );
};

export default App;
